import React from "react";
import { useParams } from "react-router-dom";
import { instance } from "../../axios";
import OrganizeDetails from "../organize/organize-details";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Button,
  TextField,
  Stack,
  Grid,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";

export default function UserDetails() {
  const { id } = useParams();
  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [notFound, setNotFound] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const [form, setForm] = React.useState({});

  React.useEffect(() => {
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const res = await instance.get(`/users/${id}`);
        if (!res || res.data == null) {
          setUser(null);
          setNotFound(true);
        } else {
          // console.log("fetched user:", res.data);
          setUser(res.data);
          setForm(res.data);
        }
      } catch (err) {
        if (err?.response?.status === 404) setNotFound(true);
        else console.error("fetch user failed", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const handleOrganizeUpdated = (updatedUser) => {
    if (updatedUser) {
      setUser(updatedUser);
      setForm(updatedUser);
    }
  };

  const handleResetPassword = async () => {
    alert("Reset password clicked");
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    try {
      const res = await instance.put(`/users/${id}`, form);

      setUser(res.data);
      setForm(res.data);
      setEditing(false);
    } catch (err) {
      console.error("update user failed", err);
      alert("Failed to save changes");
    }
  };

  if (loading) return <div>Loading...</div>;
  if (notFound) return <div>User not found</div>;
  if (!user) return <div>No user data</div>;

  const footerButtonStyle = {
    height: 36,
    minHeight: 36,
    display: "flex",
    alignItems: "center",
  };

  // base height for both modes
  const baseFieldSx = {
    "& .MuiInputBase-root": {
      height: 48,
    },
  };

  // edit: clear visible border
  const editFieldSx = {
    ...baseFieldSx,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(0,0,0,0.35)",
    },
  };

  // show: keep border super soft AND freeze hover/focus styles
  const showFieldSx = {
    ...baseFieldSx,
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "rgba(0,0,0,0.05)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(0,0,0,0.05)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "rgba(0,0,0,0.05)",
      },
    },
  };

  const readOnlyInputProps = { readOnly: true };

  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        // alignItems: "center",
      }}>
      <Typography variant="h5" gutterBottom>
        Contact info
      </Typography>

      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1.5,
          p: 2,
          mb: 3,
          backgroundColor: "background.paper",
          transition: "all 0.2s ease",
          minHeight: 280,
          width: 600,
          maxWidth: "90vw",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}>
        <Box>
          <Grid container spacing={2} sx={{ mt: 0.2 }}>
            {/* Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Name"
                name="name"
                value={editing ? form.name || "" : user.name || ""}
                onChange={editing ? handleChange : undefined}
                variant="outlined"
                fullWidth
                size="small"
                sx={editing ? editFieldSx : showFieldSx}
                InputProps={editing ? undefined : readOnlyInputProps}
              />
            </Grid>

            {/* Firstname */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Firstname"
                name="firstname"
                value={editing ? form.firstname || "" : user.firstname || ""}
                onChange={editing ? handleChange : undefined}
                variant="outlined"
                fullWidth
                size="small"
                sx={editing ? editFieldSx : showFieldSx}
                InputProps={editing ? undefined : readOnlyInputProps}
              />
            </Grid>

            {/* Phone */}
            <Grid item xs={12}>
              <TextField
                label="Phone"
                name="phoneNumber"
                value={
                  editing ? form.phoneNumber || "" : user.phoneNumber || ""
                }
                onChange={editing ? handleChange : undefined}
                variant="outlined"
                fullWidth
                size="small"
                sx={editing ? editFieldSx : showFieldSx}
                InputProps={editing ? undefined : readOnlyInputProps}
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12}>
              <TextField
                label="Email"
                name="email"
                value={editing ? form.email || "" : user.email || ""}
                onChange={editing ? handleChange : undefined}
                variant="outlined"
                fullWidth
                size="small"
                type="email"
                sx={editing ? editFieldSx : showFieldSx}
                InputProps={editing ? undefined : readOnlyInputProps}
              />
            </Grid>
          </Grid>
        </Box>

        {/* <Divider sx={{ my: 1 }} /> */}

        {/* footer */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 2,
            minHeight: 40,
          }}>
          {editing ? (
            <Stack direction="row" spacing={1} sx={footerButtonStyle}>
              <Button
                variant="contained"
                size="small"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                sx={footerButtonStyle}>
                Save
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<CloseIcon />}
                onClick={() => {
                  setForm(user);
                  setEditing(false);
                }}
                sx={footerButtonStyle}>
                Cancel
              </Button>
            </Stack>
          ) : (
            <Tooltip title="Edit user info">
              <IconButton
                onClick={() => setEditing(true)}
                aria-label="edit user"
                sx={footerButtonStyle}>
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}

          {editing ? (
            <Button
              variant="outlined"
              size="small"
              onClick={handleResetPassword}
              sx={footerButtonStyle}>
              Reset password
            </Button>
          ) : (
            <span style={{ width: 120, height: 36 }} />
          )}
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        <OrganizeDetails user={user} onUpdated={handleOrganizeUpdated} />
      </Box>
    </Box>
  );
}
